import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import type { PropsWithChildren } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { recordSentryError } from '@/src/lib/sentry';
import { getErrorMessage, shouldReportError } from '@/src/utils/errors';

type ToastVariant = 'success' | 'error' | 'info';

type ToastInput = {
  actionLabel?: string;
  durationMs?: number | null;
  message: string;
  onPress?: () => void;
  title?: string;
  variant?: ToastVariant;
};

type ToastState = {
  actionLabel?: string;
  id: number;
  message: string;
  onPress: () => void;
  title: string;
  variant: ToastVariant;
};

type ToastContextValue = {
  hideToast: () => void;
  showError: (error: unknown, fallback?: string) => void;
  showSuccess: (message: string, title?: string) => void;
  showToast: (toast: ToastInput) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

function ToastIcon({ variant }: { variant: ToastVariant }) {
  if (variant === 'success') {
    return <CircleCheck color={colors.surface} size={20} strokeWidth={2.5} />;
  }

  if (variant === 'error') {
    return <CircleAlert color={colors.surface} size={20} strokeWidth={2.5} />;
  }

  return <Info color={colors.surface} size={20} strokeWidth={2.5} />;
}

export function ToastProvider({ children }: PropsWithChildren) {
  const insets = useSafeAreaInsets();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  const hideToast = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setToast(null);
  }, []);

  const showToast = useCallback(
    ({
      actionLabel,
      durationMs = 4200,
      message,
      onPress,
      title,
      variant = 'info',
    }: ToastInput) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      setToast({
        id: Date.now(),
        actionLabel,
        message,
        onPress: onPress ?? hideToast,
        title: title ?? (variant === 'error' ? 'Something went wrong' : ''),
        variant,
      });

      if (durationMs !== null) {
        timeoutRef.current = setTimeout(() => {
          setToast(null);
          timeoutRef.current = null;
        }, durationMs);
      } else {
        timeoutRef.current = null;
      }
    },
    [hideToast],
  );

  const showSuccess = useCallback(
    (message: string, title = 'Saved') => {
      showToast({ message, title, variant: 'success' });
    },
    [showToast],
  );

  const showError = useCallback(
    (error: unknown, fallback?: string) => {
      if (shouldReportError(error)) {
        recordSentryError(
          error,
          fallback ? `toast_error:${fallback}` : 'toast_error',
        );
      }
      showToast({
        message: getErrorMessage(error, fallback),
        title: 'Action needed',
        variant: 'error',
      });
    },
    [showToast],
  );

  return (
    <ToastContext.Provider
      value={{ hideToast, showError, showSuccess, showToast }}
    >
      {children}
      <View
        pointerEvents="box-none"
        style={[
          styles.viewport,
          {
            paddingTop: insets.top + spacing.sm,
          },
        ]}
      >
        {toast ? (
          <Pressable
            accessibilityRole="alert"
            onPress={() => {
              toast.onPress();
              hideToast();
            }}
            style={[
              styles.toast,
              toast.variant === 'success' ? styles.toastSuccess : null,
              toast.variant === 'error' ? styles.toastError : null,
              toast.variant === 'info' ? styles.toastInfo : null,
            ]}
          >
            <ToastIcon variant={toast.variant} />
            <View style={styles.toastText}>
              {toast.title ? (
                <Text style={styles.title}>{toast.title}</Text>
              ) : null}
              <Text style={styles.message}>{toast.message}</Text>
            </View>
            {toast.actionLabel ? (
              <Text style={styles.actionLabel}>{toast.actionLabel}</Text>
            ) : (
              <X color={colors.surface} size={18} strokeWidth={2.5} />
            )}
          </Pressable>
        ) : null}
      </View>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within ToastProvider.');
  }

  return context;
}

const styles = StyleSheet.create({
  viewport: {
    alignItems: 'center',
    left: 0,
    paddingHorizontal: spacing.md,
    pointerEvents: 'box-none',
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: 1000,
  },
  toast: {
    alignItems: 'flex-start',
    borderRadius: 8,
    elevation: 4,
    flexDirection: 'row',
    gap: spacing.sm,
    maxWidth: 640,
    padding: spacing.md,
    shadowColor: '#000000',
    shadowOffset: { height: 6, width: 0 },
    shadowOpacity: 0.16,
    shadowRadius: 14,
    width: '100%',
  },
  toastError: {
    backgroundColor: colors.danger,
  },
  toastInfo: {
    backgroundColor: colors.secondary,
  },
  toastSuccess: {
    backgroundColor: colors.success,
  },
  toastText: {
    flex: 1,
    gap: 2,
  },
  title: {
    color: colors.surface,
    fontSize: 14,
    fontWeight: '900',
  },
  message: {
    color: colors.surface,
    fontSize: 13,
    lineHeight: 18,
  },
  actionLabel: {
    alignSelf: 'center',
    color: colors.surface,
    fontSize: 13,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
});
